/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/************************************************************************************

COMPONENT: GLOBAL CODE

*************************************************************************************/

window.ON_CHANGE_DEBOUNCE_TIMER = 300

window.PUB_SUB_EVENTS = {
	cartUpdate: 'cart-update',
	quantityUpdate: 'quantity-update',
	optionValueSelectionChange: 'option-value-selection-change',
	variantChange: 'variant-change',
	productGroupChange: 'product-group-change',
	cartError: 'cart-error',
	library: {
		carousel: 'carousel-loaded',
	},
}

let subscribers = {}

function subscribe(eventName, callback) {
	if (subscribers[eventName] === undefined) {
		subscribers[eventName] = []
	}

	subscribers[eventName] = [...subscribers[eventName], callback]

	return function unsubscribe() {
		subscribers[eventName] = subscribers[eventName].filter((cb) => {
			return cb !== callback
		})
	}
}

function publish(eventName, data) {
	if (subscribers[eventName]) {
		subscribers[eventName].forEach((callback) => {
			callback(data)
		})
	}
}

class SectionId {
	static #separator = '__'

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
	static parseId(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[0]
	}

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
	static parseSectionName(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[1]
	}

	// for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
	static getIdForSection(sectionId, sectionName) {
		return `${sectionId}${SectionId.#separator}${sectionName}`
	}
}

class HTMLUpdateUtility {
	/**
	 * Used to swap an HTML node with a new node.
	 * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
	 *
	 * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
	 */
	static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
		preProcessCallbacks?.forEach((callback) => callback(newContent))

		const newNodeWrapper = document.createElement('div')
		HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
		const newNode = newNodeWrapper.firstChild

		// dedupe IDs
		const uniqueKey = Date.now()
		oldNode.querySelectorAll('[id], [form]').forEach((element) => {
			element.id && (element.id = `${element.id}-${uniqueKey}`)
			element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`)
		})

		oldNode.parentNode.insertBefore(newNode, oldNode)
		oldNode.style.display = 'none'

		postProcessCallbacks?.forEach((callback) => callback(newNode))

		setTimeout(() => oldNode.remove(), 500)
	}

	// Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
	static setInnerHTML(element, html) {
		element.innerHTML = html
		element.querySelectorAll('script').forEach((oldScriptTag) => {
			const newScriptTag = document.createElement('script')
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value)
			})
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
		})
	}
}

const trapFocusHandlers = {}

function l_trapFocus(container, defaultElementToFocus) {
	const elements = l_getFocusableElements(container)
	const first = elements[0]
	const last = elements[elements.length - 1]
	const elementToFocus = defaultElementToFocus || first

	l_removeTrapFocus()

	trapFocusHandlers.focusin = (event) => {
		if (event.target !== container && event.target !== last && event.target !== first) return

		document.addEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.focusout = function () {
		document.removeEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
		// On the last focusable element and tab forward, focus the first element.
		if (event.target === last && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if ((event.target === container || event.target === first) && event.shiftKey) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', trapFocusHandlers.focusout)
	document.addEventListener('focusin', trapFocusHandlers.focusin)

	elementToFocus.focus()

	if (
		elementToFocus.tagName === 'INPUT' &&
		['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
		elementToFocus.value
	) {
		elementToFocus.setSelectionRange(0, elementToFocus.value.length)
	}
}
function l_getFocusableElements(container) {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	).filter((element) => !element.closest('[aria-hidden="true"]'))
}
function l_removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', trapFocusHandlers.focusin)
	document.removeEventListener('focusout', trapFocusHandlers.focusout)
	document.removeEventListener('keydown', trapFocusHandlers.keydown)

	if (elementToFocus) elementToFocus.focus()
}
function l_pauseAllMedia() {
	document.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*')
	})
	document.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	document.querySelectorAll('video').forEach((video) => video.pause())
	document.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
	document.querySelectorAll('l-media-video').forEach((video) => {
		video.player.pause()
	})
}
function l_debounce(fn, wait) {
	let t
	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}
function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}
function l_getSectionDOM(html, selector = '.shopify-section') {
	return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
}
function formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', '.')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}
function l_lockBodyScroll() {
	document.documentElement.classList.add('js-block-page')
}
function l_unLockBodyScroll() {
	document.documentElement.classList.remove('js-block-page')
}
function l_fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}
function l_loadJS(src, target = document.body, async = false, defer = true) {
	return new Promise((resolve, reject) => {
		const doc = target.ownerDocument
		const currScript = doc.querySelector(`script[src="${src}"]`)
		if (currScript) {
			if (currScript.dataset.loaded) return resolve(true)
			currScript.addEventListener('load', () => {
				currScript.dataset.loaded = true
				resolve(true)
			})
			return
		}
		const script = doc.createElement('script')
		script.src = src
		script.async = async
		script.defer = defer
		script.addEventListener('load', () => {
			script.dataset.loaded = true
			resolve(true)
		})
		script.onerror = reject
		target.appendChild(script)
	})
}
function l_loadCSS(href, target = document.head) {
	return new Promise((resolve, reject) => {
		const doc = target.ownerDocument
		const currLink = doc.querySelector(`link[href="${href}"]`)
		if (currLink) {
			if (currLink.dataset.loaded) return resolve(true)
			currLink.addEventListener('load', () => {
				currLink.dataset.loaded = true
				resolve(true)
			})
			return
		}
		const link = doc.createElement('link')
		link.rel = 'stylesheet'
		link.href = href
		link.addEventListener('load', () => {
			link.dataset.loaded = true
			resolve(true)
		})
		link.onerror = reject
		target.appendChild(link)
	})
}
function l_setupVariablesBeforeOpenModal() {
	var l_headerSectionEl = l_headerSectionEl || document.querySelector('.section-header')
	var l_announcementBarSectionEl = l_announcementBarSectionEl || document.querySelector('.section-announcement-bar')

	document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${l_headerSectionEl?.scrollHeight || 0}px`)
	document.documentElement.style.setProperty(
		'--l-announcement-bar-height',
		`${l_announcementBarSectionEl?.scrollHeight || 0}px`
	)
}
function l_blockPosition(element) {
	if (!element) return
	const currentPosition = element.getBoundingClientRect()
	element.style.position = 'absolute'
	element.style.top = `${currentPosition.top}px`
	element.style.left = `${currentPosition.left}px`
	element.style.width = `${currentPosition.width}px`
	element.style.height = `${currentPosition.height}px`

	requestAnimationFrame(() => {
		element.style.position = ''
		element.style.top = ''
		element.style.left = ''
		element.style.width = ''
		element.style.height = ''
	})
}

const L_ATC = {
	cartDrawer: null,
	handleConfigWithFormEl(formEl, sections = []) {
		const config = l_fetchConfig('javascript')
		config.headers['X-Requested-With'] = 'XMLHttpRequest'
		delete config.headers['Content-Type']

		const formData = new FormData(formEl)
		formData.append('sections', ['mini-cart', ...sections])

		config.body = formData

		return config
	},
	handleConfigWithVariantId(addingData) {
		// Setup config object
		const config = l_fetchConfig('javascript')
		config.headers['X-Requested-With'] = 'XMLHttpRequest'
		delete config.headers['Content-Type']

		// Setup form data
		const formData = new FormData()
		formData.append('sections', ['mini-cart'])

		addingData.forEach((item, index0) => {
			formData.append(`items[${index0}][quantity]`, item.quantity)
			formData.append(`items[${index0}][id]`, item.id)
		})

		// Insert formData into config
		config.body = formData

		return config
	},
	addToCart(config, id = '234na08fa3', element = document) {
		return fetch(window.themeVariables.routes.cartAddUrl, config)
			.then((response) => response.json())
			.then((response) => {
				// ATC: Handle error case
				if (response.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: response.description } }))

					return response
				}

				element.dispatchEvent(
					new CustomEvent('variant:added', {
						bubbles: true,
						detail: {
							variant: response.hasOwnProperty('items') ? response['items'][0] : response,
						},
					})
				)

				// Theme functionalities
				fetch(`${window.themeVariables.routes.cartUrl}.js`).then(async (response2) => {
					const cartContent = await response2.json()

					document.documentElement.dispatchEvent(
						new CustomEvent('cart:updated', {
							bubbles: true,
							detail: {
								cart: cartContent,
								id,
								response,
							},
						})
					)

					cartContent['sections'] = response['sections']

					document.documentElement.dispatchEvent(
						new CustomEvent('cart:refresh', {
							bubbles: true,
							detail: {
								cart: cartContent,
								openMiniCart: window.themeVariables.settings.cartType === 'drawer',
							},
						})
					)
				})

				// Publish added to cart event
				document.dispatchEvent(new CustomEvent('l:cart:updated', { bubbles: true, detail: { id, response } }))

				return response
			})
	},
}

/************************************************************************************

COMPONENT: ACCESSIBILITY BUTTON
+ POSITION: footer
+ Feature: Change accessibility from widget to text link

*************************************************************************************/
// if (!customElements.get('accessibility-button')) {
// 	customElements.define(
// 		'accessibility-button',
// 		class AccessibilityButton extends HTMLElement {
// 			constructor() {
// 				super()
// 			}

// 			connectedCallback() {
// 				this.showEyePopup()
// 			}

// 			showEyePopup() {
// 				if (!window.__audioEyeInitialized) {
// 					document.documentElement.classList.add('l-audioEye-hide')
// 					this.innerHTML = 'Please wait...'

// 					let isLoaded = false

// 					const detectAdioEyeFn = () => {
// 						if (window.__audioEyeInitialized && !isLoaded) {
// 							l_debounce(() => {
// 								this.innerHTML = 'Accessibility Settings'

// 								this.addEventListener('click', () => {
// 									window.AudioEye.activate()
// 									document.documentElement.classList.remove('l-audioEye-hide')
// 								})

// 								isLoaded = true
// 							}, 3000)()
// 						}
// 					}

// 					for (let index = 1; index <= 28; index++) {
// 						const timeFrame = index * 500

// 						l_debounce(() => {
// 							detectAdioEyeFn()

// 							if (index + 1 == 28 && !isLoaded) {
// 								this.innerHTML = 'Accessibility Settings'
// 								document.documentElement.classList.remove('l-audioEye-hide')
// 							}
// 						}, timeFrame)()
// 					}

// 					return
// 				}
// 			}
// 		}
// 	)
// }

if (!customElements.get('accessibility-button')) {
	customElements.define(
		'accessibility-button',
		class AccessibilityButton extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.innerHTML = 'Accessibility Settings'

				this.addEventListener('click', () => {
					const launcher = document.querySelector('#ae_launcher')

					if (launcher) {
						launcher.click()
					}
				})
			}
		}
	)
}


/************************************************************************************

COMPONENT: ACCESSIBILITY BUTTON
+ POSITION: footer
+ Feature: Change accessibility from widget to text link

*************************************************************************************/
if (!customElements.get('l-header-customer-icon')) {
	customElements.define(
		'l-header-customer-icon',
		class LTAHeaderCustomerIcon extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Data
				if (!this.classList.contains('is-logged-in')) return

				// Elements
				const customerLinkEl = this.querySelector('[data-customer-link]')

				customerLinkEl?.addEventListener('click', (event) => {
					event.preventDefault()
					this.classList.toggle('is-open')
				})

				document.addEventListener('click', ({ target }) => {
					if (!this.contains(target) && !customerLinkEl?.contains(target)) {
						this.classList.remove('is-open')
					}
				})
			}
		}
	)
}

/************************************************************************************

* [GENERAL COMPONENTS]

*************************************************************************************/
if (!customElements.get('l-swiper-carousel')) {
	customElements.define(
		'l-swiper-carousel',
		class LTASwiperCarousel extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				if (window.Swiper) this.setupCarousel()
				else {
					document.addEventListener('carousel:loaded', () => {
						this.setupCarousel()
					})
				}
			}

			setupCarousel() {
				this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

				if (!this.carouselConfig) return

				this.classList.remove('is-placeholder')
				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
			}

			slideTo(index = 0) {
				this.swiperCarousel.slideTo(index)
			}
		}
	)
}

if (!customElements.get('l-modal')) {
	customElements.define(
		'l-modal',
		class LTAModal extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.triggerEls = document.querySelectorAll(`[data-modal-trigger="${this.dataset.modalId}"]`)
				this.closeEl = this.querySelector('[data-close-modal]')
				this.modalInnerEl = this.querySelector('[data-modal-inner]')

				// Data
				this.customClass = ''

				// Setup events
				this.addEventListener('click', (event) => {
					if (!this.modalInnerEl.contains(event.target)) {
						this.close()
					}
				})

				this.triggerEls.forEach((triggerEl) => {
					triggerEl.addEventListener('click', (event) => {
						event.preventDefault()

						this.customClass = triggerEl.dataset.customClass

						console.clear()
						console.log(this.customClass)

						this.open()
					})
				})

				this.closeEl.addEventListener('click', this.close.bind(this))
			}

			open() {
				this.classList.add('open')
				this.customClass && this.classList.add(this.customClass)
				l_lockBodyScroll()
			}

			close() {
				this.classList.remove('open')
				this.customClass && this.classList.remove(this.customClass)
				this.customClass = ''
				l_unLockBodyScroll()
			}
		}
	)
}

if (!customElements.get('l-product-card')) {
	customElements.define(
		'l-product-card',
		class LTAProductCard extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.atcBtnEls = Array.from(this.querySelectorAll('[data-atc]'))
				this.variantsEl = this.querySelector('[data-variants]')
				this.toggleVariantsEl = this.querySelector('[data-toggle-variants]')
				this.productCardModalEl = document.getElementById(this.dataset.productCardModalId)
				this.atcBtnControlEls = this.querySelectorAll('[data-atc-controls]')

				// Data
				this.productId = this.dataset.productId
				this.hasQuantityLimit = this.hasAttribute('data-quantity-limit')
				this.quantityLimit = parseInt(this.dataset.quantityLimit)

				// Get ATC button elements from the modal if it exists
				if (this.productCardModalEl) {
					const modalATCEls = this.productCardModalEl.querySelectorAll('[data-atc]')

					this.atcBtnEls.push(...modalATCEls)
				}

				// Setup Add To Cart Events
				this.atcBtnEls.forEach((atcBtnEl) => {
					atcBtnEl.addEventListener('click', () => {
						const variantId = atcBtnEl.dataset.variantId

						// Enable loading
						atcBtnEl.classList.add('is-loading')

						// Setup form data
						const config = L_ATC.handleConfigWithVariantId([{ id: variantId, quantity: 1 }])

						// Add selected variant to cart
						L_ATC.addToCart(config, 'l-product-upsell-card', atcBtnEl).finally(() => {
							atcBtnEl.classList.remove('is-loading')

							if (this.toggleVariantsEl) {
								this.variantsEl.classList.remove('is-open')
								this.toggleVariantsEl.classList.remove('is-open')
							}

							const modalEl = atcBtnEl.closest('popover-content')
							if (modalEl) {
								modalEl.open = false
							}
						})
					})
				})

				if (this.toggleVariantsEl) {
					this.toggleVariantsEl.addEventListener('click', () => {
						this.variantsEl.classList.toggle('is-open')
						this.toggleVariantsEl.classList.toggle('is-open')
					})
				}

				// Toggle UI based on quantity in cart
				if (this.hasQuantityLimit) {
					document.addEventListener('cart:updated', ({ detail, target }) => {
						const cart = detail.cart
						let hiddenATCControl = true

						// Check product in newest cart
						const productInCart = cart.items.find((item) => {
							return `${item.product_id}` == this.productId
						})

						// No current product in cart OR quantity limit not reached
						if (!productInCart || (productInCart && productInCart.quantity < this.quantityLimit)) {
							hiddenATCControl = false
						}

						this.atcBtnControlEls.forEach((atcBtnControlEl) => {
							atcBtnControlEl.classList.toggle('l-hidden', hiddenATCControl)
						})
					})
				}
			}
		}
	)
}

if (!customElements.get('l-drawer')) {
	class LTADrawer extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			this.setupDrawer()
		}

		setupDrawer() {
			// Elements
			this.triggerEls = document.querySelectorAll(this.dataset.triggerSelector)
			this.closeBtnEls = this.querySelectorAll('[data-close]')
			this.drawerInnerEl = this.querySelector('[data-drawer-inner]')
			this.triggerEl = null

			// Open events
			this.triggerEls.forEach((triggerEl) => {
				triggerEl.addEventListener('click', () => {
					this.drawer_open(triggerEl)
				})
			})

			// Close events
			this.closeBtnEls.forEach((closeBtnEl) => {
				closeBtnEl.addEventListener('click', () => {
					this.drawer_close()
				})
			})
		}

		drawer_open(triggerEl) {
			l_setupVariablesBeforeOpenModal()
			l_lockBodyScroll()

			// Open drawer
			this.classList.remove('l-hidden')

			requestAnimationFrame(() => {
				this.classList.add('is-active')
			})

			// Setup focus trap on main content
			if (triggerEl) {
				this.triggerEl = triggerEl
				l_trapFocus(this.drawerInnerEl)
			}

			// Set global events
			window.addEventListener('click', this.drawer_closeOutsideOnClick.bind(this))
			window.addEventListener('keyup', this.drawer_closeOutsideOnEscape.bind(this))
		}

		drawer_close() {
			l_unLockBodyScroll()

			// Close drawer
			this.classList.remove('is-active')

			window.l_debounce(() => {
				this.classList.add('l-hidden')
			}, 400)()

			// Remove focus trap
			if (this.triggerEl) {
				l_removeTrapFocus(this.triggerEl)
				this.triggerEl = null
			}

			// Remove global events
			window.removeEventListener('click', this.drawer_closeOutsideOnClick)
			window.removeEventListener('keyup', this.drawer_closeOutsideOnEscape)
		}

		drawer_closeOutsideOnClick(event) {
			const target = event.target
			const isOutsideClick =
				!this.drawerInnerEl.contains(target) && !Array.from(this.triggerEls).some((el) => el.contains(target))

			if (isOutsideClick) this.drawer_close()
		}

		drawer_closeOutsideOnEscape(event) {
			if (event.code.toUpperCase() === 'ESCAPE') {
				this.drawer_close()
			}
		}
	}

	customElements.define('l-drawer', LTADrawer)
}

